import { DateTime } from 'luxon';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import Order from '../../../models/order/Order';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import Paths from '../../../types/Paths';
import ProductTypeClass from '../../../types/ProductTypeClass';
import RequestState from '../../../types/RequestState';
import generatePath from '../../../util/generatePath';
import OrderCloneButton from '../OrderCloneButton';
import OrderLink from '../OrderLink';
import OrderStatusLabel from '../OrderStatusLabel';

const OrderPreview = ({ accountStore, cartStore, routeService, order }) => {
  const getPhysicalProducts = useCallback(() => {
    return order.products.filter(
      (orderProduct) => orderProduct.type === ProductTypeClass.PHYSICAL
    );
  }, []);

  const physicalProducts = getPhysicalProducts();

  const getOrderStatus = () => {
    if (!order.last_status) {
      return null;
    }

    return <OrderStatusLabel status={order.last_status} />;
  };

  const getProductCount = () => {
    return (
      <FormattedMessage
        {...globalTranslations.productCount}
        values={{ count: order.number_of_products }}
      />
    );
  };

  const getOrderReference = () => {
    const reference = order.orderReference;

    return (
      reference && (
        <FormattedMessage
          {...globalTranslations.yourReferenceSentence}
          values={{ reference }}
        />
      )
    );
  };

  const getPath = () => {
    return routeService.getPath(
      generatePath(Paths.MyAccountOrder, { id: order.id })
    );
  };

  const getOrderViewButton = () => {
    return (
      <Link to={getPath()}>
        <Button className="OrderPreview__button-view" color="primary">
          <FormattedMessage {...globalTranslations.toOrder} />
        </Button>
      </Link>
    );
  };

  const getOrderDate = () => {
    return DateTime.fromISO(order.date_purchased).toLocaleString();
  };

  const shippingInfo = order.shippingAddress;

  return (
    <div className="OrderPreview">
      <Row className="OrderPreview__row">
        <Col className="OrderPreview__order-information">
          <div className="OrderPreview__order-number">
            <span className="OrderPreview__link">
              <OrderLink id={order.id} link={getPath()} />
            </span>
          </div>
          <div className="OrderPreview__order-date">
            <FormattedMessage
              id="myAccount.orderDate"
              defaultMessage="Ordered on: {orderDate}"
              values={{
                orderDate: getOrderDate(),
              }}
            />
          </div>
          <div className="OrderPreview__customer">
            {shippingInfo && (
              <>
                <div className="OrderPreview__customer-name">
                  {shippingInfo.name}
                </div>
                <div className="OrderPreview__customer-address">
                  {shippingInfo.street_address}
                </div>
                <div className="OrderPreview__customer-area">
                  <span className="OrderPreview__customer-zip">
                    {shippingInfo.postalcode}
                  </span>
                  <span className="OrderPreview__customer-city">
                    {shippingInfo.city}
                  </span>
                </div>
              </>
            )}
          </div>
          {physicalProducts && physicalProducts.length > 0 && (
            <div className="OrderPreview__count">{getProductCount()}</div>
          )}
          {order.orderReference && (
            <div className="OrderPreview__order-reference">
              {getOrderReference()}
            </div>
          )}
          <div className="OrderPreview__status">{getOrderStatus()}</div>
        </Col>
        <Col xs="auto" className="OrderPreview__order-checkout">
          <div className="OrderPreview__buttons">
            {getOrderViewButton()}
            <OrderCloneButton
              order={order}
              disabled={!cartStore.state === RequestState.LOADING}
            />
          </div>
          <div className="OrderPreview__price">
            <span className="OrderPreview__total-sentence">
              <FormattedMessage {...globalTranslations.totalPriceSentence} />
              {':'}
            </span>
            <span className="OrderPreview__total-price">
              {!accountStore.isRetailer && order.totalPrice.text}
              {accountStore.isRetailer && order.taxFreeTotal.text}
            </span>
          </div>
        </Col>
      </Row>
    </div>
  );
};

OrderPreview.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  order: modelOf(Order),
  intl: intlShape.isRequired,
};

export default inject(
  'routeService',
  'accountStore',
  'cartStore'
)(injectIntl(observer(OrderPreview)));
